import { z } from "zod/v4";

import {
  MarketShippingOptionTypeSchema,
  MarketShippingTransitionSchema,
  ShippingModelSchema,
} from "./shopify-shipping";

export const ShippingMoneySchema = z.object({
  amount: z.string(),
  currencyCode: z.string(),
});

const weight = z.object({ value: z.number(), unit: z.string() });

export const MarketShippingRateSchema = z.object({
  id: z.string(),
  price: ShippingMoneySchema,
  transitTimeMinSeconds: z.number().nullable(),
  transitTimeMaxSeconds: z.number().nullable(),
  minValue: ShippingMoneySchema.optional(),
  maxValue: ShippingMoneySchema.nullable().optional(),
  minWeight: weight.optional(),
  maxWeight: weight.nullable().optional(),
});

export type MarketShippingRate = z.infer<typeof MarketShippingRateSchema>;

export const MarketShippingRateGroupSchema = z.object({
  id: z.string(),
  conditions: z.object({
    collections: z
      .array(z.object({ id: z.string(), title: z.string() }))
      .nullable(),
    originLocations: z.array(z.object({ id: z.string(), name: z.string() })),
  }),
  rates: z.array(MarketShippingRateSchema),
  carrier: z
    .object({
      carrierService: z.object({ id: z.string(), name: z.string().nullable() }),
      absoluteAdjustment: ShippingMoneySchema.nullable(),
      percentageAdjustment: z.number().nullable(),
      autoIncludeNewServices: z.boolean(),
      serviceConfiguration: z.array(
        z.object({ name: z.string(), status: z.string() })
      ),
    })
    .optional(),
});

export type MarketShippingRateGroup = z.infer<
  typeof MarketShippingRateGroupSchema
>;

export const MarketShippingOptionSchema = z.object({
  id: z.string(),
  optionType: z.enum([
    ...MarketShippingOptionTypeSchema.options,
    "unsupported",
  ]),
  sourceType: z.string(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  currency: z.string(),
  isActive: z.boolean(),
  freeDeliveryMinimumValue: ShippingMoneySchema.nullable(),
  rateGroups: z.array(MarketShippingRateGroupSchema),
});

export type MarketShippingOption = z.infer<typeof MarketShippingOptionSchema>;

// Shared analysis input contains coverage only, never Shopify mutation fields.
export const MarketShippingPreservationOptionSchema =
  MarketShippingOptionSchema.pick({
    id: true,
    optionType: true,
    currency: true,
    isActive: true,
    freeDeliveryMinimumValue: true,
  }).extend({
    rateGroups: z.array(
      z.object({
        id: z.string(),
        rates: z.array(
          MarketShippingRateSchema.omit({ price: true }).extend({
            transitTimeMinSeconds: z.number().nullable().default(null),
            transitTimeMaxSeconds: z.number().nullable().default(null),
          })
        ),
      })
    ),
  });

export type MarketShippingPreservationOption = z.infer<
  typeof MarketShippingPreservationOptionSchema
>;

export const MarketShippingEligibilitySchema = z.object({
  eligible: z.boolean(),
  reason: z.string().nullable(),
});

export const MarketShippingStateSchema = z.enum([
  "inherited",
  "disabled",
  "enabled",
]);

export const MarketShippingSummarySchema = z.object({
  state: MarketShippingStateSchema,
  eligibility: MarketShippingEligibilitySchema,
  id: z.string(),
  name: z.string(),
  handle: z.string(),
  status: z.string(),
  shipping: z
    .object({
      isEnabled: z.boolean(),
      optionCount: z.number(),
      currency: z.string().nullable().optional(),
    })
    .nullable(),
});

export type MarketShippingSummary = z.infer<typeof MarketShippingSummarySchema>;

export const MarketShippingConfigurationSchema =
  MarketShippingSummarySchema.extend({
    shipping: z
      .object({
        isEnabled: z.boolean(),
        options: z.array(MarketShippingOptionSchema),
      })
      .nullable(),
  });

export type MarketShippingConfiguration = z.infer<
  typeof MarketShippingConfigurationSchema
>;

export const MarketShippingRegionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("country"), countryCode: z.string() }),
  z.object({
    kind: z.literal("province"),
    countryCode: z.string(),
    provinceCode: z.string(),
  }),
]);

export type MarketShippingRegion = z.infer<typeof MarketShippingRegionSchema>;

/** Regional coverage and the shipping configurations used by active markets. */
export const MarketShippingRegionConfigurationSchema =
  MarketShippingConfigurationSchema.pick({
    id: true,
    name: true,
    status: true,
    shipping: true,
  }).extend({
    primary: z.boolean(),
    regions: z.array(MarketShippingRegionSchema),
  });

export type MarketShippingRegionConfiguration = z.infer<
  typeof MarketShippingRegionConfigurationSchema
>;

/**
 * What Shopify itself reports for the store: the shipping model and the
 * granted scopes. `canWrite` here means only that the write scope is granted.
 */
export const ShopifyShippingCapabilitiesSchema = z.object({
  shippingModel: ShippingModelSchema,
  grantedScopes: z.array(z.string()),
  canRead: z.boolean(),
  canWrite: z.boolean(),
  missingReadScopes: z.array(z.string()),
  missingWriteScopes: z.array(z.string()),
});

export type ShopifyShippingCapabilities = z.infer<
  typeof ShopifyShippingCapabilitiesSchema
>;

/**
 * One shipping backup the merchant can act on, with the actions the journal
 * would actually allow. Computed server-side so the surface cannot offer a
 * resolution the journal will refuse.
 */
export const ShippingStuckBackupSchema = z.object({
  recordId: z.string(),
  marketId: z.string(),
  status: z.string(),
  reason: z.string().optional(),
  /** Every backup this resolution would cover, newest first. */
  recordIds: z.array(z.string()),
  canRetry: z.boolean(),
  canAbandon: z.boolean(),
  blockedReason: z.string().optional(),
});

export type ShippingStuckBackup = z.infer<typeof ShippingStuckBackupSchema>;

/** Persisted recovery state: outstanding backups, journal rows and plans. */
export const ShippingRecoveryStateSchema = z.object({
  state: z.enum(["ready", "required"]),
  reason: z.string().optional(),
  backupIds: z.array(z.string()),
  /** Backups awaiting a merchant decision. Empty when nothing is stuck. */
  stuck: z.array(ShippingStuckBackupSchema).default([]),
});

export type ShippingRecoveryState = z.infer<typeof ShippingRecoveryStateSchema>;

/**
 * The Shopify read composed with recovery state. Here `canWrite` is the
 * effective answer: scope granted, no upgrade pause, recovery ready, and no
 * model transition queued or running. `transition` is the only place the
 * frontend reads preparation progress from.
 */
export const ShippingCapabilitiesSchema =
  ShopifyShippingCapabilitiesSchema.extend({
    recovery: ShippingRecoveryStateSchema,
    transition: MarketShippingTransitionSchema.nullable(),
  });

export type ShippingCapabilities = z.infer<typeof ShippingCapabilitiesSchema>;
